/**
 * Table coordinates: where something is on the table, as opposed to where it
 * is on one screen (shared/protocol/gesture.ts defines them for the wire).
 *
 * Each player sees the same table from their own seat, and the trick box is not
 * square — it is wider than it is deep — so a pixel offset turned a quarter
 * round to the side seats lands in the wrong place: "above the north card" on
 * one screen came out part of the way there on the others. Table coordinates
 * are anchored on the things a player is actually aiming at, the card slots:
 *
 *   across   −1, +1  the centres of the slots on the sender's left and right
 *   along    −1      the centre of the far slot, across the table
 *             0      the middle of the trick
 *            +1      the centre of the sender's own slot
 *            +2      the sender's own hand
 *
 * and each axis is piecewise linear between those anchors on every screen, so a
 * card held over a slot is over that slot everywhere, however far each screen
 * has to carry it to get there.
 */
import type { Position } from './view'

export interface Frame {
  /** Middle of the trick, viewport px. */
  readonly cx: number
  readonly cy: number
  /** Middle to the centre of the left and right slots. */
  readonly sx: number
  /** Middle to the centre of the far and near slots. */
  readonly sy: number
}

let cache: { key: string; cw: number; ch: number } | null = null

/** The trick box on this screen, measured. Null off the play table. */
export function measureFrame(): Frame | null {
  const trick = document.querySelector<HTMLElement>('.play-felt .trick')
  if (!trick) return null
  const r = trick.getBoundingClientRect()
  if (!r.width || !r.height) return null
  // The trick card's size is a token, and a token is only a length once it is
  // on an element; measure it once per box size rather than on every move.
  const key = `${r.width.toFixed(1)}x${r.height.toFixed(1)}`
  if (cache?.key !== key) {
    const probe = document.createElement('div')
    probe.style.cssText = 'position:absolute;visibility:hidden;width:var(--trick-card-w);height:var(--trick-card-h)'
    trick.appendChild(probe)
    cache = { key, cw: probe.offsetWidth, ch: probe.offsetHeight }
    probe.remove()
  }
  return {
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
    sx: Math.max(1, r.width / 2 - cache.cw / 2),
    sy: Math.max(1, r.height / 2 - cache.ch / 2),
  }
}

/** Where a seat's card lies in the trick, from the middle, on this screen. */
export function slotOffset(position: Position, f: Frame): [number, number] {
  switch (position) {
    case 'n':
      return [0, -f.sy]
    case 's':
      return [0, f.sy]
    case 'w':
      return [-f.sx, 0]
    case 'e':
      return [f.sx, 0]
  }
}

/** Towards the sender, and to the sender's right, on this screen, by where
 *  the sender sits on it. */
const AXES: Record<Position, { along: [number, number]; across: [number, number] }> = {
  s: { along: [0, 1], across: [1, 0] },
  n: { along: [0, -1], across: [-1, 0] },
  w: { along: [-1, 0], across: [0, 1] },
  e: { along: [1, 0], across: [0, -1] },
}

function spans(position: Position, f: Frame): { along: number; across: number } {
  const vertical = position === 's' || position === 'n'
  return vertical ? { along: f.sy, across: f.sx } : { along: f.sx, across: f.sy }
}

/**
 * A point on this screen, in the viewer's own table coordinates (the viewer is
 * the one at the bottom). `hand` is how far the viewer's own hand is from the
 * middle, in px, which is where `along` reaches 2.
 */
export function toTable(px: number, py: number, f: Frame, hand: number): { x: number; y: number } {
  const d = py - f.cy
  const s = f.sy
  const y = d <= s ? d / s : 1 + (d - s) / Math.max(1, hand - s)
  return { x: (px - f.cx) / f.sx, y }
}

/**
 * A point in the sender's table coordinates, on this screen, where the sender
 * sits at `position` and their hand is `hand` px from the middle along the
 * line towards them.
 */
export function fromTable(x: number, y: number, position: Position, f: Frame, hand: number): [number, number] {
  const { along, across } = AXES[position]
  const span = spans(position, f)
  const d = y <= 1 ? y * span.along : span.along + (y - 1) * Math.max(1, hand - span.along)
  const a = x * span.across
  return [f.cx + along[0] * d + across[0] * a, f.cy + along[1] * d + across[1] * a]
}

/** How far a point is from the middle along the line towards `position`. */
export function alongFrom(position: Position, f: Frame, px: number, py: number): number {
  const { along } = AXES[position]
  return (px - f.cx) * along[0] + (py - f.cy) * along[1]
}
